import { Alert, Box, FormControlLabel, FormLabel, Radio, RadioGroup, useTheme } from "@mui/material"
import "./radio-button-group.scss"
import { useId } from "react"
import PropTypes from "prop-types"
import { RegimeDraftLockTooltip } from "routes/regime/regime-draft-locking/regime-draft-lock-tooltip"

RadioButtonGroup.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    setterFunction: PropTypes.func,
    options: PropTypes.array,
}

/**
 * A group of Mui Radios made to look like buttons - one option selectable
 *
 * @param label
 * @param value - The current selected value and the value being set with the setter function.
 * @param sx
 * @param labelSx
 * @param {function} setterFunction - The setter function to run when the button is clicked, setting the value
 * @param optionSx
 * @param options - value/label object array, label is text displayed in the button, value is the value set in the
 *     setter function
 * @returns A nicely formatted group of buttons.
 */
export function RadioButtonGroup({ label, value, sx, labelSx, setterFunction, optionSx, options }) {
    const theme = useTheme()
    const id = useId()

    if (window.location.href.includes("localhost") && (!label || label.trim().length <= 3)) {
        return <Alert severity="error">Radio group needs a label</Alert>
    }

    return (
        <Box className={"radio-button-group-container"}>
            <FormLabel
                sx={{
                    marginTop: 0,
                    marginLeft: 1,
                    backgroundColor: theme.palette.background.default,
                    width: "max-content",
                    px: 0.5,
                    ...labelSx,
                }}
                id={id}
            >
                {label}
            </FormLabel>
            <RadioGroup
                aria-labelledby={id}
                className={"radio-button-group"}
                sx={{
                    justifyContent: "space-between",
                    borderRadius: 1,
                    padding: 1,
                    gap: 0.5,
                    outline: `1px solid ${
                        theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300]
                    }`,
                    marginTop: -1,
                    ...sx,
                }}
                row
                value={value}
                name="radio-buttons-group"
                onChange={handleChange}
            >
                {options.map((option) => (
                    <RegimeDraftLockTooltip key={option.value}>
                        <FormControlLabel
                            sx={{
                                px: 1.5,
                                py: 1,
                                margin: 0,
                                flexGrow: 1,
                                justifyContent: "center",
                                borderRadius: 1,
                                ...optionSx,
                            }}
                            className="radio-button-label"
                            data-cy={`radio-button-label-${option.value}`}
                            value={option.value}
                            control={<Radio checked={option.value === value} />}
                            label={option.label}
                            tabIndex={-1}
                        />
                    </RegimeDraftLockTooltip>
                ))}
            </RadioGroup>
        </Box>
    )

    function handleChange(event) {
        setterFunction(event.target.value)
    }
}
